// hooks/useEpicConnect.js
//
// Shared logic for the "Connect my health records" flow — used by both
// ConnectEpicButton (dashboard) and Care Copilot's profile mode. Handles
// checking connection status, kicking off the OAuth redirect, and
// completing the token exchange once the patient is redirected back.

import { useCallback, useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { useAuth0 } from './useAuth0';
import { apiService } from '../services/api';
import { generateCodeVerifier, generateCodeChallenge, generateState } from '../utils/pkce';

const NATIVE_REDIRECT_URI = 'com.verainsight.nephrotrax://epic-callback';
// Dynamic, not hardcoded — must match whatever origin the app is actually
// running on (localhost:3000 during dev, app.nephrotrax.com in
// production), or Epic redirects back to a domain with no active session.
// Every origin used here (including localhost) must ALSO be registered as
// a redirect URI on the Epic client record itself, or Epic will reject the
// authorize request outright.
const WEB_REDIRECT_URI = `${window.location.origin}/epic-callback`;

// Feature flag — see ConnectEpicButton.js for details. When disabled, this
// hook skips its network calls entirely (not just hiding the UI), so a
// build with Epic disabled makes zero Epic-related requests.
const EPIC_ENABLED = process.env.REACT_APP_ENABLE_EPIC !== 'false';

export function useEpicConnect() {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const [connected, setConnected] = useState(false);
  const [checking, setChecking] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const checkStatus = useCallback(async () => {
    if (!EPIC_ENABLED) {
      setChecking(false);
      setConnected(false);
      return;
    }
    setChecking(true);
    try {
      const data = await apiService.getEpicConnectionStatus();
      setConnected(!!data.connected);
    } catch (e) {
      setConnected(false);
    } finally {
      setChecking(false);
    }
  }, []);

  /**
   * Manually re-trigger a sync for an already-connected patient — for use
   * after backend changes affect what gets pulled/mapped (e.g. broader lab
   * matching), or just as a periodic "refresh my data" action, without
   * requiring a full disconnect/reconnect through Epic again.
   */
  const syncNow = useCallback(async () => {
    setSyncing(true);
    try {
      const result = await apiService.syncEpicRecords();
      return result;
    } finally {
      setSyncing(false);
    }
  }, []);

  useEffect(() => {
    if (!EPIC_ENABLED) {
      setChecking(false);
      return;
    }
    // Wait for Auth0 to finish loading AND confirm the user is actually
    // authenticated before checking Epic status — otherwise this fires
    // before AppRoutes has registered the token getter (child effects run
    // before parent effects on mount in React), producing a spurious 401
    // that has nothing to do with whether the user is really logged in.
    if (authLoading) return;
    if (!isAuthenticated) {
      setChecking(false);
      setConnected(false);
      return;
    }
    checkStatus();
  }, [authLoading, isAuthenticated, checkStatus]);

  const connectEpic = useCallback(async () => {
    if (!EPIC_ENABLED) return;
    setConnecting(true);
    try {
      const isNative = Capacitor.isNativePlatform();
      const redirectUri = isNative ? NATIVE_REDIRECT_URI : WEB_REDIRECT_URI;

      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = generateState();

      // Survives the redirect on both web (tab persists) and native
      // (WebView persists — Browser.open() opens a separate system view
      // temporarily, it doesn't destroy the app's WebView).
      sessionStorage.setItem('epic_code_verifier', codeVerifier);
      sessionStorage.setItem('epic_state', state);
      sessionStorage.setItem('epic_redirect_uri', redirectUri);

      const { authorizeUrl } = await apiService.getEpicAuthorizeUrl({
        redirectUri,
        codeChallenge,
        state,
      });

      if (isNative) {
        await Browser.open({ url: authorizeUrl, windowName: '_self' });
      } else {
        window.location.href = authorizeUrl;
      }
    } catch (e) {
      console.error('Failed to start Epic connect flow', e);
      setConnecting(false);
    }
  }, []);

  /**
   * Call this with the full redirect URL once the patient lands back in
   * the app — either from EpicCallbackPage (web) or the native deep-link
   * listener registered once at the app root (see App.js instructions).
   */
  const handleEpicCallback = useCallback(async (fullUrl) => {
    try {
      const params = new URL(fullUrl).searchParams;
      const code = params.get('code');
      const returnedState = params.get('state');
      const expectedState = sessionStorage.getItem('epic_state');
      const codeVerifier = sessionStorage.getItem('epic_code_verifier');
      const redirectUri = sessionStorage.getItem('epic_redirect_uri');

      if (!code || !expectedState || returnedState !== expectedState) {
        console.error('Epic callback state mismatch — aborting, possible CSRF or stale session');
        setConnecting(false);
        return false;
      }

      await apiService.exchangeEpicCode({ code, codeVerifier, redirectUri });

      sessionStorage.removeItem('epic_code_verifier');
      sessionStorage.removeItem('epic_state');
      sessionStorage.removeItem('epic_redirect_uri');

      setConnected(true);
      setConnecting(false);

      // Sync is now a separate, explicit step (via the "Sync Now" button)
      // rather than auto-firing immediately after connect. This means a
      // successful connection is never retroactively reported as "failed"
      // just because the sync step hit an issue right after — the patient
      // stays connected either way, and can retry sync independently.
      return true;
    } catch (e) {
      console.error('Epic token exchange failed', e);
      setConnecting(false);
      return false;
    }
  }, []);

  return { connected, checking, connecting, syncing, connectEpic, handleEpicCallback, checkStatus, syncNow };
}